import { Name, createName, isNameEqual } from "./Name";
import { Study, createStudy, updateStudy } from "./Study";
import {
  dateFromDicomString,
  nameFromDicomString,
  readDicomFile,
  sexFromDicomString,
  stringFromDataset,
} from "../commons/dicom";
import { stringOrEmpty } from "../commons/utils";
import moment from "moment";

/**
 * The patient.
 */
export interface Patient {
  /** Patient ID (0010,0020) */
  id: string;
  /** Patient's Name (0010,0010) */
  name: Name;
  /** Patient's Birth Date (0010,0030) */
  birthdate: string | null;
  /** Patient's Sex (0010,0040) */
  sex: string;
  /** The specific characterset (0008,0005)*/
  charset: string;
  /** The Other Patient ID (0010,1000)*/
  otherid: string;
  /** The patient's studies */
  studies: Study[];
}

/**
 * Patients info (without study hierachy)
 */
export interface PatientInfo {
  /** Patient's Name (0010,0010) */
  name: Name | undefined;
  /** Patient's Birth Date (0010,0030) */
  birthdate: string | null;
}

/**
 * Create a new patient object.
 * @param id The patient id (object). Default: "".
 * @param name The name (object). Default: empty name.
 * @param birthdate The date of birth. Default: 1970-01-01.
 * @param sex The patient gender. Default: ""
 * @param charset The specific character set used to encode strings. Default: ""
 * @param otherid: string;
 * @param studies The studies. Default: []
 * @returns A new patient object.
 */
export const createPatient = (
  id: string = "",
  name: Name = createName(),
  sex: string = "",
  charset: string = "",
  otherid: string = "",
  studies: Study[] = []
): Patient => ({
  id: id,
  name: name,
  birthdate: null,
  sex: sex,
  charset: charset,
  otherid: otherid,
  studies: studies,
});

/**
 * Update the patient and studies from a DICOM dataset.
 * @param patient The patient object to be updated.
 * @param dataset The DICOM dataset.
 */
export const updatePatient = (dataset: any, patient?: Patient) => {
  if (patient === undefined) return;

  // update patient properties (optional)
  updateID(patient, dataset);
  updateName(patient, dataset);
  updateBirthdate(patient, dataset);
  updateSex(patient, dataset);
  updateCharset(patient, dataset);

  // Study Instance UID (required)
  const studyuid = stringOrEmpty(dataset.string("x0020000d"));
  if (studyuid.length === 0)
    throw new Error("invalid DICOM Object: missing Study Instance UID");

  // find study for study uid
  let index = patient.studies.findIndex((study) => study.uid === studyuid);
  if (index === -1) {
    // not found ... add new study
    index = patient.studies.push(createStudy(studyuid)) - 1;
  }

  updateStudy(patient.studies[index], dataset);
};

export const extractPatientInfos = async (
  files: File[]
): Promise<PatientInfo[]> => {
  let patients: PatientInfo[] = [];
  for (let i = 0; i < files.length; i++) {
    const ds = await readDicomFile(files[i]);
    if (ds === undefined) continue;
    const namestring = stringFromDataset(ds, "x00100010");
    const birthdate = dateFromDicomString(
      stringOrEmpty(ds.string("x00100030"))
    );

    // get Name from string
    let name: Name | undefined = undefined;
    if (namestring !== undefined) name = nameFromDicomString(namestring);

    // check we already have this patient
    if (
      !patients.some(
        (patient) =>
          isNameEqual(patient.name, name) && patient.birthdate === birthdate
      )
    ) {
      // add (not found)
      patients.push({ name: name, birthdate: birthdate });
    }
  }
  return patients;
};

export const patientStudy = (uid: string, patient?: Patient) => {
  return patient?.studies.find((study) => study.uid === uid);
};

/////////////////////////////////////////////////////////////

const updateID = (patient: Patient, dataset: any) => {
  let id: string = stringOrEmpty(dataset.string("x00100020"));
  if (id.length !== 0) patient.id = id;
};

const updateName = (patient: Patient, dataset: any) => {
  let name = stringFromDataset(dataset, "x00100010");
  if (!name) return;
  name = name.trim();
  if (name.length !== 0 && patient.name.familyName.length === 0)
    patient.name = nameFromDicomString(name);
};

const updateBirthdate = (patient: Patient, dataset: any) => {
  const birthdate = dateFromDicomString(
    stringOrEmpty(dataset.string("x00100030"))
  );
  // only set if current date is the dafault date or new date is older
  if (
    birthdate &&
    (!patient.birthdate ||
      (patient.birthdate &&
        moment(birthdate).isBefore(moment(patient.birthdate))))
  )
    patient.birthdate = birthdate;
};

const updateSex = (patient: Patient, dataset: any) => {
  if (patient.sex.length === 0)
    patient.sex = sexFromDicomString(
      stringOrEmpty(dataset.string("x00100040"))
    );
};

const updateCharset = (patient: Patient, dataset: any) => {
  if (patient.charset.length === 0)
    patient.charset = stringOrEmpty(dataset.string("x00080005"));
};
